import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Search,
  ChevronDown,
  ChevronUp,
  Printer,
  RefreshCw,
  User,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchInventarioClientes, fetchInventarioPorConductor } from '@/services/inventarioClientes.service';
import { getClientGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const InventarioPendienteRuta = ({ idLavanderia, onCountChange }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const printer = getPrinterInstance();

  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [printingClientId, setPrintingClientId] = useState(null);

  const isConductor = String(user?.role ?? user?.rol ?? '').toLowerCase() === 'conductor';

  // IDs de clientes asignados al conductor (mismo patrón que RecoleccionRopaSucia)
  const clientesAsignadosIds = useMemo(() => {
    const arr = user?.raw?.clientesAsignados;
    if (!Array.isArray(arr)) return null;
    return arr.map((c) => (typeof c === 'string' ? c : String(c?.id || c?._id || '')));
  }, [user]);

  useEffect(() => {
    loadInventario();
  }, [idLavanderia]);

  const loadInventario = async () => {
    setIsLoading(true);
    try {
      const fetchInventario =
        isConductor && Array.isArray(clientesAsignadosIds) && clientesAsignadosIds.length > 0
          ? fetchInventarioPorConductor(idLavanderia, clientesAsignadosIds)
          : fetchInventarioClientes({
              query: { 'lavanderia.id': idLavanderia, estado: 'Activo' },
              limit: 500,
              page: 1,
            });

      const [response, garmentResponse] = await Promise.all([
        fetchInventario,
        getClientGarments({ limit: 500 }),
      ]);

      const familiaMap = new Map();
      (garmentResponse.data || []).forEach((g) => {
        familiaMap.set(String(g._id), g.familia || null);
      });

      const items = response.data || [];
      const grouped = {};
      items.forEach((item) => {
        const clienteId = item.cliente?.id || item.cliente?._id || 'sin-cliente';
        const clienteNombre = item.cliente?.nombre || 'Sin Cliente';
        if (!grouped[clienteId]) {
          grouped[clienteId] = { clienteId, clienteNombre, items: [], totalPrendas: 0 };
        }
        const tipoPrendaId = item.tipoPrenda?.id || item.tipoPrenda?._id;
        const familia = familiaMap.get(String(tipoPrendaId)) || null;
        grouped[clienteId].items.push({ ...item, familia });
        grouped[clienteId].totalPrendas += item.cantidad || 0;
      });

      const lista = Object.values(grouped).sort((a, b) =>
        a.clienteNombre.localeCompare(b.clienteNombre)
      );

      setClientes(lista);
      onCountChange?.(lista.length);
    } catch (error) {
      console.error('Error al cargar inventario pendiente:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario',
        variant: 'destructive',
      });
      setClientes([]);
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (clienteId) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      next.has(clienteId) ? next.delete(clienteId) : next.add(clienteId);
      return next;
    });
  };

  const handlePrint = async (cliente) => {
    setPrintingClientId(cliente.clienteId);
    try {
      if (!printer.isConnected()) {
        toast({ title: 'Conectando impresora...', description: 'Selecciona tu impresora Bluetooth' });
        await printer.connect();
      }
      toast({ title: 'Imprimiendo...', description: `Inventario de ${cliente.clienteNombre}` });
      await printer.printInventarioCliente(cliente);
      toast({ title: 'Listo', description: 'Inventario impreso exitosamente' });
    } catch (error) {
      const raw = (error?.message || '').toLowerCase();
      const msg =
        raw.includes('user cancelled') || raw.includes('user denied')
          ? 'Impresión cancelada'
          : 'No se pudo imprimir. Verifica la conexión Bluetooth.';
      toast({ title: 'Aviso', description: msg, variant: 'destructive' });
    } finally {
      setPrintingClientId(null);
    }
  };

  const filtered = clientes.filter(
    (c) => !searchTerm || c.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <p className="text-sm text-gray-500 mt-4">Cargando inventario...</p>
      </div>
    );
  }

  // Conductor sin clientes asignados
  if (isConductor && Array.isArray(clientesAsignadosIds) && clientesAsignadosIds.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Sin clientes asignados</h3>
          <p className="text-sm text-gray-500">
            No tienes clientes asignados. Solicita al administrador que te asigne clientes desde la parametrización de usuarios.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barra de búsqueda */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm h-10"
              />
            </div>
            <Button
              size="icon"
              variant="outline"
              className="h-10 w-10 shrink-0"
              onClick={loadInventario}
              title="Recargar"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {filtered.length} cliente(s)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Lista vacía */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Sin inventario pendiente
            </h3>
            <p className="text-sm text-gray-500">
              No hay prendas en inventario activo para esta lavandería
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {filtered.map((cliente) => {
            const isExpanded = expandedClients.has(cliente.clienteId);
            const isPrinting = printingClientId === cliente.clienteId;

            // Agrupar items del cliente por familia (mismo patrón que RecoleccionRopaSucia)
            const itemsPorFamilia = (() => {
              const groups = new Map();
              cliente.items.forEach((item) => {
                const key = item.familia?.nombre || 'Sin Familia';
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(item);
              });
              return Array.from(groups.entries()).sort(([a], [b]) => {
                if (a === 'Sin Familia') return 1;
                if (b === 'Sin Familia') return -1;
                return a.localeCompare(b);
              });
            })();

            return (
              <motion.div
                key={cliente.clienteId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="border-l-4 border-l-blue-400">
                  <CardContent className="p-3">
                    {/* Encabezado de cliente */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-500 shrink-0" />
                          <span className="font-medium text-sm text-gray-900 truncate">
                            {cliente.clienteNombre}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {cliente.totalPrendas} prendas
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {itemsPorFamilia.length} familia(s)
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 text-blue-600 border-blue-200 hover:bg-blue-50"
                          onClick={() => handlePrint(cliente)}
                          disabled={isPrinting}
                          title="Imprimir inventario"
                        >
                          {isPrinting ? (
                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600" />
                          ) : (
                            <Printer className="h-3.5 w-3.5" />
                          )}
                        </Button>

                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => toggleExpand(cliente.clienteId)}
                          title={isExpanded ? 'Contraer' : 'Ver detalle'}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Detalle expandible agrupado por familia */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.15 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 pt-3 border-t space-y-3">
                            {itemsPorFamilia.map(([familiaName, items]) => {
                              const totalGrupo = items.reduce((s, i) => s + (i.cantidad || 0), 0);
                              return (
                                <div key={familiaName}>
                                  {/* Cabecera de familia */}
                                  <div className="flex items-center justify-between px-1 mb-1.5">
                                    <div className="flex items-center gap-2">
                                      <div className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                                      <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wide">
                                        {familiaName}
                                      </span>
                                    </div>
                                    <Badge className="bg-blue-100 text-blue-800 text-[10px]">
                                      {totalGrupo} uds
                                    </Badge>
                                  </div>
                                  {/* Items del grupo */}
                                  <div className="space-y-1 pl-2">
                                    {items.map((item) => (
                                      <div
                                        key={item._id}
                                        className="flex items-center justify-between text-sm p-2 bg-blue-50 rounded-lg"
                                      >
                                        <span className="truncate flex-1 font-medium text-gray-800">
                                          {item.tipoPrenda?.nombre || item.descripcion || 'Prenda'}
                                        </span>
                                        <span className="font-semibold text-blue-700 shrink-0 ml-2">
                                          {item.cantidad}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                            {/* Total general */}
                            <div className="flex items-center justify-between text-sm p-2 bg-blue-100 rounded-lg font-semibold border-t">
                              <span className="text-blue-900">Total</span>
                              <span className="text-blue-900">{cliente.totalPrendas} prendas</span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InventarioPendienteRuta;
